/**
 * Excel Export Utility for Reports
 * Creates styled Excel files using ExcelJS
 */

import ExcelJS from 'exceljs';
import type { MinisterInRequest, MinistersListReportFilters, ActiveMinister } from '../types';
import type { MinisterSystemDegreeCount } from '../hooks/useMinistersListReport';

// ─── Colours ─────────────────────────────────────────────────────────────────
const C = {
  headerBg:  '1A3A5C',
  headerFg:  'FFFFFF',
  titleBg:   '1565C0',
  titleFg:   'FFFFFF',
  sectionBg: 'E3F2FD',
  sectionFg: '0D47A1',
  rowAlt:    'F5F7FA',
  totalBg:   'E8F5E9',
  totalFg:   '1B5E20',
};

type Fill = ExcelJS.FillPattern;
function solidFill(hex: string): Fill {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${hex}` } };
}

function styleHeader(row: ExcelJS.Row, bgHex = C.headerBg, fgHex = C.headerFg) {
  row.height = 20;
  row.eachCell({ includeEmpty: true }, (cell) => {
    cell.fill = solidFill(bgHex);
    cell.font = { bold: true, color: { argb: `FF${fgHex}` }, size: 10 };
    cell.alignment = { vertical: 'middle', wrapText: false };
    cell.border = { bottom: { style: 'thin', color: { argb: 'FF9E9E9E' } } };
  });
}

/**
 * Export ministers list to Excel with styling
 */
export async function exportMinistersListToExcel(
  ministers: MinisterInRequest[],
  filters: MinistersListReportFilters,
  systemDegreeCounts: MinisterSystemDegreeCount[] = [],
  uncredentialedMinisters: ActiveMinister[] = []
): Promise<void> {
  const sorted = [...ministers].sort((a, b) =>
    (a.minister_name ?? "").localeCompare(b.minister_name ?? "", "es", { sensitivity: "base" })
  );
  const sortedUncredentialed = [...uncredentialedMinisters].sort((a, b) =>
    (a.full_name ?? "").localeCompare(b.full_name ?? "", "es", { sensitivity: "base" })
  );

  const wb = new ExcelJS.Workbook();
  wb.creator = 'ADB Admin Portal';
  wb.created = new Date();

  // ==============================================================
  // SHEET 1: SUMMARY
  // ==============================================================
  const summaryWs = wb.addWorksheet('Resumen');
  summaryWs.columns = [{ width: 40 }, { width: 35 }];

  const totalMinisters = sorted.length;
  const uniqueMinisters = new Set(sorted.map((m) => m.minister_id)).size;
  const requests = new Set(sorted.map((m) => m.credential_request_id)).size;

  const addSummaryRow = (label: string, value?: string | number, bold = false) => {
    const row = summaryWs.addRow([label, value ?? '']);
    if (bold) {
      row.getCell(1).font = { bold: true };
      row.getCell(2).font = { bold: true };
    }
    return row;
  };

  // Title
  const titleRow = summaryWs.addRow(['REPORTE DE MINISTROS EN SOLICITUDES DE CREDENCIALES', '']);
  summaryWs.mergeCells(titleRow.number, 1, titleRow.number, 2);
  titleRow.height = 24;
  titleRow.getCell(1).fill = solidFill(C.titleBg);
  titleRow.getCell(1).font = { bold: true, color: { argb: `FF${C.titleFg}` }, size: 13 };
  titleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };

  summaryWs.addRow([]);
  addSummaryRow('Generado:', new Date().toLocaleString('es-BO'));
  summaryWs.addRow([]);

  const filtersTitle = summaryWs.addRow(['FILTROS APLICADOS', '']);
  filtersTitle.getCell(1).fill = solidFill(C.sectionBg);
  filtersTitle.getCell(1).font = { bold: true, color: { argb: `FF${C.sectionFg}` } };

  addSummaryRow('Fecha Desde:', filters.dateFrom || 'Todas');
  addSummaryRow('Fecha Hasta:', filters.dateTo || 'Todas');
  addSummaryRow('Tipos de Credencial:', filters.credentialTypes.join(', ') || 'Todos');
  addSummaryRow('Estados:', filters.statuses.join(', ') || 'Todos');

  summaryWs.addRow([]);

  const statsTitle = summaryWs.addRow(['RESUMEN', '']);
  statsTitle.getCell(1).fill = solidFill(C.sectionBg);
  statsTitle.getCell(1).font = { bold: true, color: { argb: `FF${C.sectionFg}` } };

  addSummaryRow('Total de Registros:', totalMinisters, true);
  addSummaryRow('Ministros Únicos:', uniqueMinisters, true);
  addSummaryRow('Solicitudes Únicas:', requests, true);

  summaryWs.addRow([]);
  addSummaryRow('NOTA:', 'Un ministro puede aparecer en múltiples solicitudes');

  // ==============================================================
  // SHEET 2: DETAILED MINISTERS LIST
  // ==============================================================
  const detailWs = wb.addWorksheet('Ministros Detallado', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  detailWs.columns = [
    { header: 'Nombre Completo',       width: 40 },
    { header: 'DNI',                   width: 15 },
    { header: 'Código ADB',            width: 15 },
    { header: 'Grado',                 width: 22 },
    { header: 'Código Solicitud',      width: 18 },
    { header: 'Tipo Credencial',       width: 20 },
    { header: 'Estado',                width: 15 },
    { header: 'Año',                   width: 10 },
    { header: 'Distrito (Ministro)',   width: 28 },
    { header: 'Región (Ministro)',     width: 28 },
    { header: 'Distrito (Solicitud)', width: 28 },
    { header: 'Región (Solicitud)',   width: 28 },
    { header: 'Email',                 width: 28 },
    { header: 'Celular',               width: 16 },
    { header: 'Posición',              width: 25 },
  ];

  styleHeader(detailWs.getRow(1));

  sorted.forEach((m, i) => {
    const row = detailWs.addRow([
      m.minister_name || '',
      m.dni || '',
      m.adb_code || '',
      m.degree_name || '—',
      m.request_code || '—',
      m.credential_type || '—',
      m.request_status || '—',
      m.request_year || '—',
      m.minister_district_name || '—',
      m.minister_region_name || '—',
      m.request_district_name || '—',
      m.request_region_name || '—',
      m.email || '—',
      m.cell_phone || '—',
      m.position || '—',
    ]);
    if (i % 2 === 1) {
      row.eachCell({ includeEmpty: true }, (c) => { c.fill = solidFill(C.rowAlt); });
    }
    row.height = 15;
  });

  const detailRange = detailWs.dimensions as unknown as { top: number; left: number; bottom: number; right: number };
  if (detailRange) {
    detailWs.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: 15 },
    };
  }

  // ==============================================================
  // SHEET 3: BY DEGREE
  // ==============================================================
  const degreeWs = wb.addWorksheet('Por Grado');
  const hasSystemData = systemDegreeCounts.length > 0;

  const degreeHeaders = [
    'Grado', 'En Solicitudes', '% del Total',
    ...(hasSystemData ? ['Total Sistema', '% Credencializados'] : []),
  ];
  degreeWs.columns = degreeHeaders.map((h, i) => ({
    header: h,
    width: i === 0 ? 30 : 18,
  }));
  styleHeader(degreeWs.getRow(1));

  const degreeCount = new Map<string, number>();
  sorted.forEach((m) => {
    const d = m.degree_name || 'Sin Grado';
    degreeCount.set(d, (degreeCount.get(d) || 0) + 1);
  });
  const totalInRequests = sorted.length;

  const systemMap = new Map<string, number>();
  let totalInSystem = 0;
  if (hasSystemData) {
    systemDegreeCounts.forEach((d) => {
      systemMap.set(d.degreeName, d.totalActive);
      totalInSystem += d.totalActive;
    });
  }

  Array.from(degreeCount.entries())
    .sort((a, b) => b[1] - a[1])
    .forEach(([degree, count], i) => {
      const pct = totalInRequests > 0 ? `${((count / totalInRequests) * 100).toFixed(1)}%` : '0.0%';
      const rowData = hasSystemData
        ? [degree, count, pct, systemMap.get(degree) ?? 0, (systemMap.get(degree) ?? 0) > 0 ? `${((count / (systemMap.get(degree) ?? 1)) * 100).toFixed(1)}%` : '—']
        : [degree, count, pct];
      const row = degreeWs.addRow(rowData);
      if (i % 2 === 1) {
        row.eachCell({ includeEmpty: true }, (c) => { c.fill = solidFill(C.rowAlt); });
      }
    });

  // Total row
  const totalData = hasSystemData
    ? ['TOTAL', totalInRequests, '100%', totalInSystem, totalInSystem > 0 ? `${((totalInRequests / totalInSystem) * 100).toFixed(1)}%` : '—']
    : ['TOTAL', totalInRequests, '100%'];
  const totalRow = degreeWs.addRow(totalData);
  totalRow.eachCell({ includeEmpty: true }, (c) => {
    c.fill = solidFill(C.totalBg);
    c.font = { bold: true, color: { argb: `FF${C.totalFg}` } };
  });

  // ==============================================================
  // SHEET 4: WITHOUT CREDENTIALS
  // ==============================================================
  if (sortedUncredentialed.length > 0) {
    const uncredWs = wb.addWorksheet('Sin Credencial', {
      views: [{ state: 'frozen', ySplit: 1 }],
    });
    uncredWs.columns = [
      { header: 'Nombre Completo', width: 40 },
      { header: 'DNI',             width: 15 },
      { header: 'Código ADB',      width: 15 },
      { header: 'Grado',           width: 22 },
      { header: 'Distrito',        width: 28 },
      { header: 'Región',          width: 28 },
      { header: 'Cargo',           width: 25 },
    ];
    styleHeader(uncredWs.getRow(1));

    sortedUncredentialed.forEach((m, i) => {
      const row = uncredWs.addRow([
        m.full_name || '',
        m.dni || '',
        m.adb_code || '',
        m.degree?.name || '—',
        m.district?.name || '—',
        m.region?.name || '—',
        m.position || '—',
      ]);
      if (i % 2 === 1) {
        row.eachCell({ includeEmpty: true }, (c) => { c.fill = solidFill(C.rowAlt); });
      }
      row.height = 15;
    });

    uncredWs.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: 7 },
    };
  }

  // ==============================================================
  // DOWNLOAD
  // ==============================================================
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Reporte_Ministros_${timestamp}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
